import { Box, Grid, Stack, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { MemberDeleteOptions } from 'graphql/generated/graphqlTypes';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { getSetupStatus, getVisitorGroupById } from 'utils/VisitorGroup/VisitorGroup';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import Loader from 'v2/components/Loader/Loader';
import useModalContext from 'v2/components/Modal/useModalContext';
import { SetupStep } from 'v2/components/SetupIndicator/SetupIndicator';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import useVisitorsGroups from 'v2/hooks/useVisitorsGroups/useVisitorsGroups';
import NotFound from 'v2/pages/GeneralErrors/NotFound';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import {
  canDeleteVisitorGroup,
  canManageUsersInVisitorGroup,
  canShareKeysInVisitorGroup
} from 'v2/utils/visitorGroupOrigin';
import DevicesTab from './components/DevicesTab';
import GroupInfo from './components/GroupInfo';
import Header from './components/Header';
import KeySharing from './components/KeySharing';
import Schedule from './components/Schedule';
import UsersTab from './components/UsersTab';
import DeleteGroupModal from './modals/DeleteGroupModal/DeleteGroupModal';

export const createTabsHeader = (translation: Translation): string[] => [translation.users, translation.devices];

const UserGroupDetails: React.FC = () => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const tabsHeader = createTabsHeader(translation);
  const { groupId } = useParams();
  const { showSnackbar } = useSnackbarContext();
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const { allVisitorGroups, loadingAllVisitorGroups, hasVisitorGroupsFetchError, retryFetchingVisitorGroups } =
    useVisitorsGroups({
      handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
    });
  const navigate = useNavigate();

  const visitorGroupDetails = getVisitorGroupById(allVisitorGroups, groupId ?? '');
  const { openModal, closeModal } = useModalContext();
  const { deleteVisitorGroupById, deleteVisitorGroupByIdMutationLoading } = useExternalUsers({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const handleOpenDeleteVisitorGroupModal = () => {
    if (visitorGroupDetails) {
      openModal(<DeleteGroupModal visitorGroup={visitorGroupDetails} handleDelete={handleDeleteGroup} />);
    }
  };

  const handleDeleteGroup = async (options: MemberDeleteOptions) => {
    if (!deleteVisitorGroupByIdMutationLoading && visitorGroupDetails) {
      await deleteVisitorGroupById(visitorGroupDetails.id, options);
      closeModal();
      navigate('../');
    }
  };

  const handleChangeTab = (event: React.SyntheticEvent, newIndex: number) => {
    setCurrentTabIndex(newIndex);
  };

  const isMediumScreen = useMediaQuery(theme.breakpoints.only('md'));

  const buildSetupSteps = (group: NonNullable<typeof visitorGroupDetails>): [SetupStep, ...SetupStep[]] => {
    const status = getSetupStatus(group);
    return [
      { label: translation.setup_add_users, completed: status.isAddUserComplete },
      { label: translation.setup_set_schedule, completed: status.isSetScheduleComplete },
      { label: translation.setup_add_devices, completed: status.isAddDeviceComplete }
    ];
  };

  if (loadingAllVisitorGroups) {
    return (
      <Box justifyContent="center" alignContent="center" sx={{ width: '100%', height: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  if (hasVisitorGroupsFetchError) {
    return <SomethingWentWrong onRetry={retryFetchingVisitorGroups} />;
  }

  if (!visitorGroupDetails) {
    return <NotFound />;
  }

  const canDelete = canDeleteVisitorGroup(visitorGroupDetails);
  const canManageUsers = canManageUsersInVisitorGroup(visitorGroupDetails);
  const canShareKeys = canShareKeysInVisitorGroup(visitorGroupDetails);

  return (
    <Stack spacing={4}>
      <BreadcrumbMeta
        breadcrumb={{
          title: visitorGroupDetails.name,
          crumbs: [
            {
              text: translation.users,
              path: '/users'
            },
            {
              text: translation.user_groups,
              path: '/users/groups'
            }
          ]
        }}
      />
      <Header
        visitorGroupName={visitorGroupDetails.name}
        handleOpenDeleteVisitorGroupModal={handleOpenDeleteVisitorGroupModal}
        setupSteps={buildSetupSteps(visitorGroupDetails)}
        isDeleteVisitorGroupVisible={canDelete}
      />
      <Grid container size={12} spacing={4} direction={{ xs: 'column', lg: 'row' }} wrap="nowrap">
        <Grid container size={{ md: 12, lg: 4 }} direction={'column'} wrap="nowrap">
          <Grid size={isMediumScreen ? 12 : undefined} margin="3px">
            <GroupInfo visitorGroup={visitorGroupDetails} />
          </Grid>
          {canShareKeys && (
            <Grid size={isMediumScreen ? 12 : undefined} margin="3px">
              <KeySharing visitorGroup={visitorGroupDetails} />
            </Grid>
          )}
          <Grid size={isMediumScreen ? 12 : undefined} margin="3px">
            <Schedule visitorGroup={visitorGroupDetails} />
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12, lg: 8 }} direction="column" wrap="nowrap">
          <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider }}>
            <Tabs value={currentTabIndex} onChange={handleChangeTab}>
              {tabsHeader.map((header, index) => (
                <Tab key={header} id={`tab-${index}`} label={header} />
              ))}
            </Tabs>
          </Box>
          {currentTabIndex === 0 && (
            <Box role="tabpanel" id="tabpanel-0" sx={{ minWidth: 0 }}>
              <UsersTab visitorGroup={visitorGroupDetails} isAddVisitorButtonVisible={canManageUsers} />
            </Box>
          )}
          {currentTabIndex === 1 && (
            <Box role="tabpanel" id="tabpanel-1" sx={{ minWidth: 0 }}>
              <DevicesTab visitorGroup={visitorGroupDetails} />
            </Box>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};
export default UserGroupDetails;
